import React, { useState } from 'react';
import { Icon } from '../Icon';
import './DepositScheduleSelector.css';

interface DepositScheduleSelectorProps {
  startDate?: string;
  notificationTime?: string;
  onChange: (data: { startDate: string; notificationTime: string }) => void;
  onBack: () => void;
}

export const DepositScheduleSelector: React.FC<DepositScheduleSelectorProps> = ({
  startDate = '',
  notificationTime = '09:00',
  onChange,
  onBack
}) => {
  function getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getCurrentTime(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  function isDateToday(dateString: string): boolean {
    return dateString === getTodayDate();
  }

  function isTimeInPast(timeString: string): boolean {
    if (!isDateToday(selectedDate)) {
      return false; // Для будущих дат любое время валидно
    }
    const currentTime = getCurrentTime();
    return timeString < currentTime;
  }

  const [selectedDate, setSelectedDate] = useState(startDate || getTodayDate());
  const [selectedTime, setSelectedTime] = useState(notificationTime);
  const [error, setError] = useState<string>('');

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    setError('');
    
    // Если выбрана сегодняшняя дата, проверяем время
    if (isDateToday(newDate) && isTimeInPast(selectedTime)) {
      const currentTime = getCurrentTime();
      setSelectedTime(currentTime);
    }
  };

  const handleTimeChange = (newTime: string) => {
    setSelectedTime(newTime);
    
    // Проверяем, не прошедшее ли это время для сегодняшней даты
    if (isDateToday(selectedDate) && isTimeInPast(newTime)) {
      setError('Нельзя выбрать прошедшее время');
    } else {
      setError('');
    }
  };

  const handleNext = () => {
    if (selectedDate && selectedTime && !error) {
      // Финальная проверка перед отправкой
      if (isDateToday(selectedDate) && isTimeInPast(selectedTime)) {
        setError('Нельзя выбрать прошедшее время');
        return;
      }
      onChange({ startDate: selectedDate, notificationTime: selectedTime });
    }
  };

  const getMinTime = (): string => {
    if (isDateToday(selectedDate)) {
      return getCurrentTime();
    }
    return '00:00';
  };

  const isValid = selectedDate && selectedTime && !error;

  return (
    <div className="deposit-schedule-selector step-content">
      <div className="deposit-schedule-selector__header">
        <button className="deposit-schedule-selector__back" onClick={onBack}>
          <Icon name="chevron_left" size={20} />
        </button>
        <div className="deposit-schedule-selector__header-content">
          <h2 className="deposit-schedule-selector__title">Дата и время</h2>
          <p className="deposit-schedule-selector__subtitle">
            В какое время вы хотите уведомление о пополнении?
          </p>
        </div>
      </div>

      <div className="deposit-schedule-selector__content">
        <div className="form-group">
          <label className="form-label">
            📅 Дата первого пополнения
          </label>
          <input
            type="date"
            className="form-input"
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            min={getTodayDate()}
          />
          <div className="form-hint">
            💡 Если вы уже что-то накопили ранее, пополните это через разовое пополнение
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '24px' }}>
          <label className="form-label">
            ⏰ Время уведомления
          </label>
          <input
            type="time"
            className={`form-input ${error ? 'form-input--error' : ''}`}
            value={selectedTime}
            onChange={(e) => handleTimeChange(e.target.value)}
            min={getMinTime()}
          />
          {error && (
            <div className="form-error">
              {error}
            </div>
          )}
          <div className="form-hint">
            В это время вам придет напоминание в Telegram бот
          </div>
        </div>

        <button
          className="deposit-schedule-selector__submit"
          onClick={handleNext}
          disabled={!isValid}
        >
          Сохранить
        </button>
      </div>
    </div>
  );
};

